import { Grid, Paper } from "@material-ui/core";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import { activityColors, activityStatusById, activityStatuses } from "features/activity/activityConsts";
import { useGetAllAliasesQuery } from "features/activity/activityStatusAliasSlice";
import ActivityStatusAliasView from "features/activityStatusAlias";
import { useGetAllActivityAttachmentsQuery } from 'features/attachments/attachmentSlice';
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { groupBy } from 'lodash';
import { useGetAllActivityLogNotesQuery } from 'features/notes/notesSlice';
import { selectIsClient, selectUserClientAccountId } from "features/user/userSlice";
import { useTitle } from "hooks/useTitle";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import FormatService from "services/formatService";
import { useGetAllActivityItemsQuery, useGetAllActivityTypesQuery, useGetMechanicalActivitiesQuery } from '../../../activity/activitySlice';
import { userHasOneClientAccount } from "../../../user/userSlice";
import ActivitiesWidget from "../FleetManagementDashboard/ActivitiesWidget/ActivitiesWidget";
import ActivityListTable from "../MechanicalDashboard/ActivityList/ActivityListTable";
import ApproveWorkOrderDialog from "./ApproveWorkOrder/ApproveWorkOrderDialog";
import DashboardFilters from "./DashboardFilters";
import WorkorderByStatusCountCard from "./WorkorderByStatusCountCard/WorkorderByStatusCountCard";
import WorkorderCostTotal from "./WorkorderCostTotal/WorkorderCostTotal";
import WorkorderCostTrendGraph from "./WorkorderCostTrendGraph/WorkorderCostTrendGraph";
import { workorderDashboardPageStyles } from "./WorkorderDashboardPageStyles";
import WorkorderSlaGraph from "./WorkorderSlaGraph/WorkorderSlaGraph";

const WorkorderDashboardPage = () => {
    useTitle('Workorder Dashboard');

    const { filters } = useSelector(state => state.workorderDashboardState);
    const classes = workorderDashboardPageStyles();
    let userClientAccountId = useSelector(selectUserClientAccountId);
    const hasOneAccount = useSelector(userHasOneClientAccount);
    const { workorderid } = useParams();
    const [openApproveWorkOrder, setOpenApproveWorkOrder] = useState(workorderid != null);

    let { data: mechanicalActivities, error: mechanicalActivitiesError, refetch, isFetching: isLoadingMechanicalActivities } = useGetMechanicalActivitiesQuery();
    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: activityTypes, error: activityTypesError, isFetching: isLoadingActivityTypes } = useGetAllActivityTypesQuery();
    let { data: lots, error: lotsError, isLoading: isLoadingLots } = useGetLotsQuery();
    let { data: clients, error: clientsError, isLoading: isLoadingClients } = useGetClientsQuery();
    let { data: aliases, error: aliasError, isLoading: isLoadingAlias } = useGetAllAliasesQuery();
    let { data: activityNotes } = useGetAllActivityLogNotesQuery();
    let { data: activityAttachments } = useGetAllActivityAttachmentsQuery();

    const noteLookup = groupBy(activityNotes?.filter(n => n.workOrderId != null && !n.isHotNote), 'workOrderId');
    const attachmentLookup = groupBy(activityAttachments, 'entityId');

    //const twelveMonthsAgo = moment().subtract(12, 'months');
    const filteredActivities = mechanicalActivities?.filter(v =>
        //(moment(v.activityDate, 'YYYY-MM-DD').isBetween(twelveMonthsAgo, moment(), undefined, '[]'))//This is done in the API already
        (!filters.lotId?.length || filters.lotId.includes(v.lotId))
        && (filters.isDefaultAccount ? (!filters.clientId || filters.clientId == v.clientId)
            : (!filters.clientAccountId || filters.clientAccountId == v.clientAccountId))
        && (filters.month == 0 || filters.month == v.activityMonth)
    );

    const monthSelected = filters.month != 0;
    const isClient = useSelector(selectIsClient);
    const clientAccounts = clients?.flatMap(c => c.clientAccounts);

    const mapExportData = row => {
        const data = {
            'Activity': activityItems?.find(a => a.id == row.activityItemId)?.name,
            'Activty Status': aliases?.find(a => a.activityStatusId === row.activityStatus && a.clientId === row.clientId)?.alias ?? activityStatusById?.[row.activityStatus]?.name,
            'Lot': lots?.find(l => l.id == row.lotId)?.name,
            'AssetID': row.assetId,
            'VIN': row.descriptor,
            'Within SLA': row.withinSla ? 'Yes' : 'No',
            'Activity Date': FormatService.formatDate(row.activityDate),
            'Charge': row.revenue,
            'Hot Note': FormatService.sanitizeForCsv(row.hotNote),
            'L1 Approver': row.l1ApproverName ?? '',
            'L2 Approver': row.l2ApproverName ?? '',
            'Approved Amount': row.l2ApprovalAmount ?? '',
            'Notes': noteLookup[row.workOrderId]?.map(n => FormatService.sanitizeForCsv(n.content))?.join(", ") ?? '',
            'Attachments': (() => { const c = attachmentLookup[row.id]?.length ?? 0; return c ? `${c} ${c === 1 ? 'Attachment' : 'Attachments'}` : 'No Attachments'; })(),
        }

        if (!isClient) {
            data['Cost'] = row.price
            data['Client'] = clients?.find(c => c.id == row.clientId)?.name;
        }
        return data;
    };

    const isLoadingDashboardData = isLoadingMechanicalActivities || isLoadingActivityItems || isLoadingLots || isLoadingClients || isLoadingActivityTypes;

    let clientAccountId = hasOneAccount ? parseInt(userClientAccountId?.[0]) : !!filters?.clientAccountId?.length ? filters?.clientAccountId : undefined;
    var clientId = clientAccounts?.find(a => a.id == clientAccountId)?.clientId


    return (
        <>
            <Grid container direction="row" spacing={3} >
                <Grid item xs={12}>
                    <DashboardFilters refetch={refetch} workorders={mechanicalActivities} />
                </Grid>
                <Grid item xs={2} >
                    <Paper elevation={1} className={classes.card} style={{ borderTop: `7px solid ${activityColors[activityStatuses.RequestedByClient]}` }}>
                        <WorkorderByStatusCountCard
                            loading={isLoadingDashboardData}
                            workorders={filteredActivities}
                            statuses={[
                                activityStatuses.RequestedByClient,
                                activityStatuses.RequestedByVendor,
                                activityStatuses.AutoRequested]}
                            text={'Not Started'}
                            mapExportData={mapExportData}
                            tooltipText={'These are the activities that have been recommended by the client or internal technicians, or auto-generated by the client’s SLA agreement, but are waiting for the Account Manager to schedule'}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={2}>
                    <Paper elevation={1} className={classes.card} style={{ borderTop: `7px solid ${activityColors[activityStatuses.PendingApproval]}` }}>
                        <WorkorderByStatusCountCard
                            loading={isLoadingDashboardData}
                            workorders={filteredActivities}
                            statuses={[activityStatuses.PendingApproval]}
                            text={<ActivityStatusAliasView activityIds={[activityStatuses.PendingApproval]} clientId={clientId} readonly={true} />}
                            mapExportData={mapExportData}
                            tooltipText={'These are activities for which an Account Manager has begun scheduling and pricing. An Account Manager can approve these by clicking on the table icon on the card'}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={2} >
                    <Paper elevation={1} className={classes.card} style={{ borderTop: `7px solid ${activityColors[activityStatuses.PendingClientApproval]}` }}>
                        <WorkorderByStatusCountCard
                            loading={isLoadingDashboardData}
                            workorders={filteredActivities}
                            statuses={[activityStatuses.PendingClientApproval]}
                            text={<ActivityStatusAliasView activityIds={[activityStatuses.PendingClientApproval]} clientId={clientId} readonly={true} />}
                            mapExportData={mapExportData}
                            tooltipText={'These are activities that have been approved by an Account Manager and are ready for client approval. A client user can approve these by clicking on the table icon on the card which will trigger the Team to begin working on the item'}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={2} >
                    <Paper elevation={1} className={classes.card} style={{ borderTop: `7px solid ${activityColors[activityStatuses.PricingApprovedByClient]}` }}>
                        <WorkorderByStatusCountCard
                            loading={isLoadingDashboardData}
                            workorders={filteredActivities}
                            statuses={[activityStatuses.PricingApprovedByClient, activityStatuses.InProgress]}
                            text={'Approved and In Progress'}
                            mapExportData={mapExportData}
                            tooltipText={'This is the count of work which has gone through the approval process. It includes both approved work which is not started yet, as well as work which is in progress'}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={2}>
                    <Paper elevation={1} className={classes.card} style={{ borderTop: `7px solid ${activityColors[activityStatuses.PendingInvoice]}` }}>
                        <WorkorderByStatusCountCard
                            loading={isLoadingDashboardData}
                            workorders={filteredActivities}
                            statuses={[activityStatuses.PendingInvoice]}
                            text={<ActivityStatusAliasView activityIds={[activityStatuses.PendingInvoice]} clientId={clientId} readonly={true} />}
                            mapExportData={mapExportData}
                            tooltipText={'The mechanical work is complete, and PMF is finalizing everything before invoicing'}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={2}>
                    <Paper elevation={1} className={classes.card} style={{ borderTop: `7px solid ${activityColors[activityStatuses.Completed]}` }}>
                        <WorkorderByStatusCountCard
                            loading={isLoadingDashboardData}
                            workorders={filteredActivities}
                            statuses={[activityStatuses.CompleteBilling, activityStatuses.Completed, activityStatuses.CompletedAndPaid]}
                            text={<ActivityStatusAliasView activityIds={[activityStatuses.Completed]} clientId={clientId} readonly={true} />}
                            mapExportData={mapExportData}
                            tooltipText={'The mechanical work is complete'}
                        />
                    </Paper>
                </Grid>
                {/* {canSeeFlags && <Grid item xs={2} >
                    <Paper elevation={1} className={classes.card} style={{ borderTop: `7px solid #E8A48E` }}>
                        <WorkorderByStatusCountCard
                            loading={isLoadingDashboardData}
                            workorders={filteredActivities}
                            filterDataByField='latestFlag'
                            text={'Requiring Attention'}
                            mapExportData={mapExportData}
                            additionalFilters={{ flaggedStr: { value: 'Yes' } }}//TODO, this is not a good implementation
                            tooltipText={'Work flagged for requiring a deeper dive'}
                        />
                    </Paper>
                </Grid>} */}
                <Authorize profile={permissionProfiles.DASHBOARD.WORKORDER_SLA}>
                    <Grid item xs={6}>
                        <Paper className={classes.card} elevation={1}>
                            <WorkorderSlaGraph
                                loading={isLoadingDashboardData}
                                workorders={filteredActivities}
                            />
                        </Paper>
                    </Grid>
                </Authorize>
                <Grid item xs={isClient ? 4 : 6}>
                    <Paper className={classes.card} elevation={1}>
                        {monthSelected ? <WorkorderCostTotal loading={isLoadingDashboardData} workorders={filteredActivities} /> : <WorkorderCostTrendGraph
                            loading={isLoadingDashboardData}
                            workorders={filteredActivities}
                        />}
                    </Paper>
                </Grid>
                {/* <Grid item xs={isClient ? 6 : 5}>
                    <Paper className={classes.card} elevation={1}>
                        <WorkorderCostByTypeGraph
                            loading={isLoadingDashboardData}
                            showAverage={!monthSelected}
                            workorders={filteredActivities}
                        />
                    </Paper>
                </Grid> */}
                <Grid item xs={isClient ? 8 : 12}>
                    <Paper className={classes.card} elevation={1}>
                        <ActivitiesWidget
                            tooltipInfo={'This table shows a complete list of all activity types. The numbers are the count of activities completed per month of each type. This graphic responds to the filters in its corner, as well as the filters on top of this dash'}
                            workorderWidget={true}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={12} style={{ position: 'relative' }}>
                    <ActivityListTable activities={filteredActivities} infoTextOnTable='Use the Filters to the left to narrow the search results in this table. The filters at the top of the page do not impact the "Activities" table' />
                </Grid>
            </Grid >
            <ApproveWorkOrderDialog open={openApproveWorkOrder} setOpen={setOpenApproveWorkOrder} workOrderId={workorderid} />
        </>
    );
};

export default WorkorderDashboardPage;
